import type {
  HomeRecommendationsResponse,
  HomeRecommendationPersonalization,
  HomeRecommendationProfileMode,
  JellyfinItem,
  RecommendationCategory,
  RecommendationMediaKind,
  RecommendationType,
} from "../api/jellyfin";

export type RecommendationShelf = {
  key: string;
  title: string;
  items: JellyfinItem[];
  categoryId: string;
  recommendationType: RecommendationType;
  baselineItemId?: string;
  baselineItemName?: string;
  mediaKind: RecommendationMediaKind;
  shelfPosition: number;
  feedId?: string;
  algorithmVersion?: number;
  profileMode?: HomeRecommendationProfileMode;
  personalization?: HomeRecommendationPersonalization;
};

const shelfTermLabels: Record<string, string> = {
  "adult-animation": "Adult Animation",
  "feel-good": "Lighthearted",
  "science-fiction": "Sci-Fi",
  "super-hero": "Superhero",
  "true-crime": "True Crime",
};

const shelfCombinationLabels: Record<string, string> = {
  "action+superhero": "Superhero Action",
  "animation+family": "Family Animation",
  "comedy+adult-animation": "Adult Animated Comedy",
  "crime+drama": "Crime Drama",
  "documentary+true-crime": "True Crime Documentaries",
  "horror+supernatural": "Supernatural Horror",
  "romance+feel-good": "Lighthearted Romance",
  "science-fiction+space": "Space Sci-Fi",
};

function normalizeShelfTerm(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
}

function shelfTermLabel(value: string) {
  const normalized = normalizeShelfTerm(value);
  return shelfTermLabels[normalized] ??
    normalized
      .split("-")
      .filter(Boolean)
      .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
      .join(" ");
}

function tasteShelfLabel(value: string) {
  const terms = value
    .split(/\s+\+\s+/)
    .map(normalizeShelfTerm)
    .filter(Boolean);
  if (!terms.length) return "";

  const direct = shelfCombinationLabels[terms.join("+")];
  if (direct) return direct;
  const reversed = shelfCombinationLabels[[...terms].reverse().join("+")];
  if (reversed) return reversed;

  // Unknown secondary tags are internal ranking signals, not suitable copy.
  return shelfTermLabel(terms[0]);
}

export function recommendationShelfTitle(category: RecommendationCategory) {
  const baseline = category.BaselineItemName?.trim();
  const kind = category.MediaKind === "Movie" ? "movies" : "shows";
  switch (category.RecommendationType) {
    case "SimilarToRecentlyPlayed":
      return baseline ? `Because You Watched ${baseline}` : "More Picks for You";
    case "SimilarToLikedItem":
      return baseline ? `More Like ${baseline}` : "We Think You'll Love These";
    case "HasDirectorFromRecentlyPlayed":
    case "HasLikedDirector":
      return baseline ? `From Director ${baseline}` : "Acclaimed Directors";
    case "HasActorFromRecentlyPlayed":
    case "HasLikedActor":
      return baseline ? `Featuring ${baseline}` : "Familiar Faces";
    case "Popular":
      return category.MediaKind === "Movie" ? "Trending Movies for You" : "Trending TV for You";
    case "RecentlyAdded":
      return "Fresh Picks for You";
    case "MatchesUserTaste":
    default: {
      const normalized = baseline?.toLowerCase() ?? "";
      if (normalized.includes("popular"))
        return category.MediaKind === "Movie" ? "Trending Movies for You" : "Trending TV for You";
      if (normalized.includes("recent") || normalized.includes("new"))
        return "Fresh Picks for You";
      return baseline
        ? tasteShelfLabel(baseline)
        : category.MediaKind === "Movie" ? "Recommended Movies" : "Recommended Series";
    }
  }
}

function prepare(
  categories: RecommendationCategory[],
  minimumItems: number,
): RecommendationShelf[] {
  return categories.flatMap((category, index) => {
    const seen = new Set<string>();
    const items = (category.Items ?? []).filter(
      (item) => Boolean(item.Id) && !seen.has(item.Id!) && seen.add(item.Id!),
    );
    if (items.length < minimumItems) return [];
    const categoryId =
      category.CategoryId ??
      `${category.MediaKind}:${category.RecommendationType}:${category.BaselineItemId ?? category.BaselineItemName ?? index}`;
    return [{
      key: `taste:${category.MediaKind}:${categoryId}`,
      title: recommendationShelfTitle(category),
      items,
      categoryId,
      recommendationType: category.RecommendationType,
      baselineItemId: category.BaselineItemId,
      baselineItemName: category.BaselineItemName,
      mediaKind: category.MediaKind,
      shelfPosition: 0,
    }];
  });
}

export function buildRecommendationShelves(
  movies: RecommendationCategory[],
  series: RecommendationCategory[],
  sessionSeed: number,
  minimumItems = 4,
) {
  const queues = {
    Movie: prepare(movies, minimumItems),
    Series: prepare(series, minimumItems),
  };
  const order: RecommendationMediaKind[] =
    sessionSeed % 2 === 0 ? ["Movie", "Series"] : ["Series", "Movie"];
  const shelves: RecommendationShelf[] = [];
  while (queues.Movie.length || queues.Series.length) {
    for (const kind of order) {
      const shelf = queues[kind].shift();
      if (shelf) shelves.push(shelf);
    }
  }
  return shelves.map((shelf, shelfPosition) => ({ ...shelf, shelfPosition }));
}

export function buildHomeRecommendationShelves(
  feed: HomeRecommendationsResponse,
  minimumItems = 4,
): RecommendationShelf[] {
  return feed.Rows.flatMap((row, index) => {
    const seen = new Set<string>();
    const items = (row.Items ?? []).filter(
      (item) => Boolean(item.Id) && !seen.has(item.Id!) && seen.add(item.Id!),
    );
    if (items.length < minimumItems) return [];
    const shelfPosition = Number.isFinite(row.Rank) ? row.Rank : index;
    return [{
      key: `home-feed:${feed.FeedId}:${row.CategoryId}`,
      title: row.Title || recommendationShelfTitle(row),
      items,
      categoryId: row.CategoryId,
      recommendationType: row.RecommendationType,
      baselineItemId: row.BaselineItemId,
      baselineItemName: row.BaselineItemName,
      mediaKind: row.MediaKind,
      shelfPosition,
      feedId: feed.FeedId,
      algorithmVersion: feed.AlgorithmVersion,
      profileMode: feed.ProfileMode,
      personalization: row.Personalization,
    }];
  }).sort((left, right) => left.shelfPosition - right.shelfPosition);
}

export function orderRecommendationShelvesForProfile(
  shelves: RecommendationShelf[],
  profileMode?: HomeRecommendationProfileMode,
) {
  const ranked = [...shelves].sort((left, right) => left.shelfPosition - right.shelfPosition);
  if (profileMode === "cold_start") {
    return ranked.filter((shelf) => shelf.personalization !== "personalized");
  }
  if (profileMode === "personalized") {
    return ranked.sort((left, right) => {
      const personalizationDelta = Number(right.personalization === "personalized")
        - Number(left.personalization === "personalized");
      return personalizationDelta || left.shelfPosition - right.shelfPosition;
    });
  }
  return ranked;
}

export function buildRecommendedMediaShelves(
  shelves: RecommendationShelf[],
  itemLimit = 20,
): RecommendationShelf[] {
  return (["Movie", "Series"] as const).flatMap((mediaKind, shelfPosition) => {
    const matching = shelves
      .filter((shelf) => shelf.mediaKind === mediaKind)
      .sort((left, right) => {
        const personalizationDelta = Number(right.personalization === "personalized")
          - Number(left.personalization === "personalized");
        return personalizationDelta || left.shelfPosition - right.shelfPosition;
      });
    const general = matching.filter((shelf) => !isContextualRecommendationShelf(shelf));
    const sources = general.length ? general : matching;
    const source = sources[0];
    if (!source) return [];

    const seen = new Set<string>();
    const items = sources
      .flatMap((shelf) => shelf.items)
      .filter((item) => Boolean(item.Id) && !seen.has(item.Id!) && seen.add(item.Id!))
      .slice(0, itemLimit);
    if (items.length < 4) return [];

    const noun = mediaKind === "Movie" ? "Movies" : "TV Shows";
    return [{
      key: `home-recommended:${mediaKind}`,
      title: `Recommended ${noun}`,
      items,
      categoryId: `home-recommended-${mediaKind.toLowerCase()}`,
      recommendationType: "MatchesUserTaste" as const,
      mediaKind,
      shelfPosition,
      feedId: source.feedId,
      algorithmVersion: source.algorithmVersion,
      profileMode: source.profileMode,
      personalization: sources.some((shelf) => shelf.personalization === "personalized")
        ? "personalized" as const
        : source.personalization,
    }];
  });
}

const CONTEXTUAL_RECOMMENDATION_TYPES = new Set<RecommendationType>([
  "SimilarToRecentlyPlayed",
  "SimilarToLikedItem",
  "HasDirectorFromRecentlyPlayed",
  "HasActorFromRecentlyPlayed",
  "HasLikedDirector",
  "HasLikedActor",
]);

export function isContextualRecommendationShelf(shelf: RecommendationShelf) {
  return CONTEXTUAL_RECOMMENDATION_TYPES.has(shelf.recommendationType) ||
    shelf.personalization === "personalized";
}

/** Keep dynamic discovery focused on explainable personal context rows. */
export function buildDiscoveryRecommendationShelves(
  shelves: RecommendationShelf[],
) {
  return shelves.filter(isContextualRecommendationShelf);
}
